import React from 'react';
import logger from '../../services/logger';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { 
            hasError: false, 
            error: null,
            errorInfo: null 
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { 
            hasError: true,
            error 
        };
    }

    componentDidCatch(error, errorInfo) {
        // Log the error to our logging service
        logger.reportError(error, {
            type: 'react-error-boundary',
            componentStack: errorInfo.componentStack,
            errorBoundary: this.props.name || 'unknown',
            props: this.props.logProps ? JSON.stringify(this.props.logProps) : undefined
        });

        // Update state with error details
        this.setState({
            error,
            errorInfo
        });

        // Call onError callback if provided
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
    }

    handleRetry = () => {
        // Log the retry attempt
        logger.userAction('click', 'error-boundary-retry', {
            errorMessage: this.state.error?.message,
            errorBoundary: this.props.name || 'unknown'
        });

        // Reset the error boundary
        this.setState({ 
            hasError: false, 
            error: null,
            errorInfo: null 
        });
    }

    handleReload = () => {
        // Log the reload attempt
        logger.userAction('click', 'error-boundary-reload', {
            errorMessage: this.state.error?.message,
            errorBoundary: this.props.name || 'unknown'
        });

        // Reload the page
        window.location.reload();
    }

    render() {
        if (this.state.hasError) {
            // Custom fallback UI if provided
            if (this.props.fallback) {
                return this.props.fallback(
                    this.state.error, 
                    this.state.errorInfo,
                    this.handleRetry,
                    this.handleReload
                );
            }

            // Default fallback UI
            return (
                <div style={{
                    padding: '20px',
                    margin: '20px',
                    border: '1px solid #ff6b6b',
                    borderRadius: '8px',
                    backgroundColor: '#ffe0e0',
                    color: '#d63384'
                }}>
                    <h2>🚫 Something went wrong</h2>
                    <p>
                        We've encountered an unexpected error. Our team has been notified 
                        and will investigate the issue.
                    </p>
                    
                    {process.env.NODE_ENV === 'development' && this.state.error && (
                        <details style={{ marginTop: '15px' }}>
                            <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                                Error Details (Development Only)
                            </summary>
                            <pre style={{
                                marginTop: '10px',
                                padding: '10px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                fontSize: '12px',
                                overflow: 'auto',
                                maxHeight: '200px'
                            }}>
                                {this.state.error.toString()}
                                {this.state.errorInfo?.componentStack}
                            </pre>
                        </details>
                    )}

                    <div style={{ marginTop: '15px' }}>
                        <button 
                            onClick={this.handleRetry}
                            style={{
                                padding: '8px 16px',
                                marginRight: '10px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Try Again
                        </button>
                        
                        <button 
                            onClick={this.handleReload}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Reload Page
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

// Higher-order component for easier usage
export const withErrorBoundary = (Component, errorBoundaryProps = {}) => {
    return function WrappedComponent(props) {
        return (
            <ErrorBoundary 
                name={Component.displayName || Component.name}
                {...errorBoundaryProps}
            >
                <Component {...props} />
            </ErrorBoundary>
        );
    };
};

// Hook for functional components to report errors
export const useErrorHandler = () => {
    return (error, errorInfo = {}) => {
        logger.reportError(error, {
            type: 'manual-error-report',
            ...errorInfo
        });
    };
};

export default ErrorBoundary; 